import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
} from '@angular/core';

import { OrbiAgentWalletType } from '@patient-ui/patient-web/store';
import {
  ORBI_PATIENT_PASS_APPLE_WALLET_LABEL,
  ORBI_PATIENT_PASS_GOOGLE_WALLET_LABEL,
  ORBI_PATIENT_PASS_INSTRUCTIONS,
  ORBI_PATIENT_PASS_TITLE,
  ORBI_PATIENT_PASS_WALLET_ERROR,
} from '@patient-ui/shared/constants';
import { OrbiPatientPassData } from '@patient-ui/shared/models';
import { resolveOrbiWalletPlatform } from '@patient-ui/shared/utils';
import { EnvironmentService } from '@patient-ui/shared-ui/utils';

/**
 * Renders the Labcorp Patient Pass card in the Orbi chat thread (PCSA-13374).
 *
 * Data + behavior mirror mobile (`orbi_chat_bubble.dart` →
 * `LabcorpPatientPassCard(compact: true)`):
 *  - The card renders ONLY when the agent returned an `lpid` — that encrypted id
 *    IS the QR code (`AppointmentsAgent.yaml`: the agent always uses the
 *    `encryptLpid` result as `lpid`). Without it there is nothing to scan, so the
 *    turn degrades to its accompanying agent text upstream.
 *  - The wallet action appears ONLY when the payload also carried a
 *    `confirmationNumber` — the guest wallet endpoint is keyed by it, and the
 *    agent omits it entirely when there is no upcoming appointment. The pass
 *    itself is NOT tied to having an appointment (agent prompt), so the QR still
 *    renders in that case; only the wallet button drops.
 *
 * The QR + wallet URL mechanics are replicated from the portal's existing
 * `PortalLabcorpIdComponent` (`{appointmentSchedulingBaseUrl}/qrcode?lpid=` and
 * the guest pass `{base}/{confirmationCode}/pass?lpid=`) rather than reusing that
 * component: it is declared in `PortalAppointmentsModule` but NOT exported, and
 * it resolves its own LPID from the patient store — whereas here the agent has
 * already supplied one, so a store round-trip would be both unnecessary and a
 * second source of truth.
 *
 * The wallet controls are the platforms' official badge artwork — the same assets
 * (and `srcset`s) the portal's Labcorp ID page serves, at their native 153x48 /
 * 175x48. WHICH badge shows follows the device (design review 08/20: "render the
 * platform-specific wallet if the OS is identifiable, otherwise show both"), via
 * `resolveOrbiWalletPlatform`: this card previously drew Apple's badge alone, so
 * every Android user was offered a wallet they do not have, and the portal page's
 * answer — always both — asks a question the device can usually answer itself.
 * Each badge image is decorative (`alt=""`) with the accessible name on its
 * button, so a control announces once rather than twice.
 */
@Component({
  selector: 'patient-ui-orbi-patient-pass-card',
  templateUrl: './orbi-patient-pass-card.component.html',
  styleUrls: ['./orbi-patient-pass-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrbiPatientPassCardComponent implements OnChanges {
  /** The typed `AppointmentPatientPass` payload from the turn. */
  @Input() data: OrbiPatientPassData | null = null;

  @Output() addToWallet = new EventEmitter<OrbiAgentWalletType>();

  /** Card copy (verbatim from mobile's compact pass card). */
  readonly title = ORBI_PATIENT_PASS_TITLE;
  readonly instructions = ORBI_PATIENT_PASS_INSTRUCTIONS;
  readonly appleWalletLabel = ORBI_PATIENT_PASS_APPLE_WALLET_LABEL;
  readonly googleWalletLabel = ORBI_PATIENT_PASS_GOOGLE_WALLET_LABEL;
  readonly walletErrorText = ORBI_PATIENT_PASS_WALLET_ERROR;

  /**
   * Which wallet badge(s) this device gets. Read from the user agent ONCE, at
   * construction: it cannot change while the card is alive, and a getter would
   * re-run the match on every change-detection pass of every pass card.
   */
  private readonly walletPlatform = resolveOrbiWalletPlatform(
    typeof navigator === 'undefined' ? null : navigator.userAgent
  );

  /** Resolved QR image URL; empty when it cannot be built. */
  qrCodeUrl = '';
  /** Resolved wallet-pass URL (guest or authenticated — see `ngOnChanges`). */
  walletUrl = '';
  /**
   * True when `walletUrl` is the GUEST endpoint. The two endpoints need
   * different download mechanics (see `onAddToWallet`), so which one was built
   * has to be remembered rather than re-derived from the URL string.
   */
  private isGuestWalletUrl = false;
  /** True while a wallet download is in flight (guards double-clicks). */
  isDownloadingWalletPass = false;
  /** Set when a download fails, so the card can say so instead of failing mute. */
  walletFailed = false;

  private static readonly QR_CODE_PATH = '/qrcode';
  private static readonly AUTHENTICATED_WALLET_PATH = '/me/pass';
  /** Portal parity: how long the guest path's re-entry guard is held. */
  private static readonly GUEST_GUARD_RELEASE_MS = 1000;

  constructor(
    private readonly environmentService: EnvironmentService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnChanges(): void {
    this.qrCodeUrl = '';
    this.walletUrl = '';
    this.isGuestWalletUrl = false;
    this.walletFailed = false;

    const lpid = this.data?.lpid?.trim();
    const baseUrl = (
      this.environmentService.appointmentSchedulingBaseUrl ?? ''
    ).replace(/\/$/, '');
    if (!lpid || !baseUrl) {
      return;
    }

    const qrUrl = new URL(
      `${baseUrl}${OrbiPatientPassCardComponent.QR_CODE_PATH}`
    );
    qrUrl.searchParams.set('lpid', lpid);
    this.qrCodeUrl = qrUrl.toString();

    // Which wallet endpoint applies depends on whether the agent returned a
    // confirmation number, exactly as `PortalLabcorpIdComponent` decides it:
    //
    //  - WITH one -> the GUEST endpoint `/{confirmationCode}/pass?lpid=`, keyed
    //    by that number.
    //  - WITHOUT one -> the AUTHENTICATED endpoint `/me/pass`, which resolves the
    //    pass from the session's own cookies and needs no appointment at all.
    //
    // Mobile hides the button in the second case (`showWalletButton:
    // confirmation.isNotEmpty`), but that is a MOBILE constraint, not a product
    // rule: it has no cookie session to spend on `/me/pass`, so it has nothing to
    // fall back to. Web does — the portal's own Labcorp ID page takes exactly this
    // fallback on its dashboard, where there is likewise no confirmation code.
    //
    // This is why the button was missing on web: the agent omits
    // `confirmationNumber` whenever there is no upcoming appointment, and the pass
    // itself is NOT tied to having one (agent prompt), so gating on it hid a
    // control the Figma frames always draw.
    const confirmationNumber = this.data?.confirmationNumber?.trim();
    if (confirmationNumber) {
      const walletUrl = new URL(
        `${baseUrl}/${encodeURIComponent(confirmationNumber)}/pass`
      );
      walletUrl.searchParams.set('lpid', lpid);
      this.walletUrl = walletUrl.toString();
      this.isGuestWalletUrl = true;
      return;
    }
    this.walletUrl = `${baseUrl}${OrbiPatientPassCardComponent.AUTHENTICATED_WALLET_PATH}`;
  }

  /** Whether there is a scannable pass to render at all. */
  get hasPass(): boolean {
    return this.qrCodeUrl.length > 0;
  }

  /** Whether there is a wallet endpoint to offer at all. */
  get canAddToWallet(): boolean {
    return this.walletUrl.length > 0;
  }

  /** Apple's badge: shown on Apple devices, and where the platform is unknown. */
  get showAppleWallet(): boolean {
    return this.canAddToWallet && this.walletPlatform !== 'google';
  }

  /** Google's badge: shown on Android, and where the platform is unknown. */
  get showGoogleWallet(): boolean {
    return this.canAddToWallet && this.walletPlatform !== 'apple';
  }

  /**
   * Download the wallet pass. BOTH badges call this, because the scheduling
   * service exposes exactly one pass endpoint — portal parity: the Labcorp ID
   * page's Apple and Google buttons also share its `downloadWalletPass()`. A true
   * Google Wallet "save" link is a signed Google Pay JWT the backend does not mint
   * today, so the Google badge serves the same `.pkpass`; that gap is the
   * service's, and it is unchanged by which badge is on screen.
   *
   * The two endpoints need DIFFERENT mechanics, and both are taken verbatim from
   * `PortalLabcorpIdComponent`:
   *
   *  - GUEST (`/{code}/pass`) takes no auth, so it goes through a synthetic
   *    anchor. A `fetch` provokes a 406 content-negotiation error there.
   *  - AUTHENTICATED (`/me/pass`) needs the JWT cookies, which a plain
   *    navigation would not attach, so it fetches with `credentials: 'include'`
   *    and saves the returned blob as a `.pkpass`.
   */
  async onAddToWallet(wallet: OrbiAgentWalletType): Promise<void> {
    if (this.isDownloadingWalletPass || !this.walletUrl) {
      return;
    }
    this.addToWallet.emit(wallet);
    this.isDownloadingWalletPass = true;
    this.walletFailed = false;

    if (this.isGuestWalletUrl) {
      this.saveAs(this.walletUrl);
      // A browser-owned navigation gives no completion signal — release the guard
      // shortly after so a retry stays possible (portal parity).
      setTimeout(() => {
        this.isDownloadingWalletPass = false;
        this.cdr.markForCheck();
      }, OrbiPatientPassCardComponent.GUEST_GUARD_RELEASE_MS);
      return;
    }

    try {
      const response = await fetch(this.walletUrl, {
        method: 'GET',
        credentials: 'include', // JWT cookies — the endpoint is authenticated
      });
      if (!response.ok) {
        throw new Error(`${response.status} ${response.statusText}`);
      }
      const objectUrl = URL.createObjectURL(await response.blob());
      this.saveAs(objectUrl, 'LabcorpPatient.pkpass');
      URL.revokeObjectURL(objectUrl);
    } catch {
      // Surfaced INLINE on the card rather than through the portal's `alert()`:
      // the panel already reports its failures in place (see the copy control's
      // retry affordance), and a modal alert over the canvas would be a second,
      // heavier idiom for the same class of error.
      this.walletFailed = true;
    } finally {
      this.isDownloadingWalletPass = false;
      this.cdr.markForCheck();
    }
  }

  /** Trigger a download/navigation via a detached anchor (portal parity). */
  private saveAs(url: string, filename?: string): void {
    const anchor = document.createElement('a');
    anchor.href = url;
    if (filename) {
      anchor.download = filename;
    }
    anchor.style.display = 'none';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  }
}
